import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Download, Upload, CheckCircle, XCircle, RefreshCw, 
  FileText, AlertTriangle, Copy, ExternalLink, Server, Key,
  Loader2, Clock, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SDIConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statistiche, setStatistiche] = useState(null);
  const [fatture, setFatture] = useState([]);
  const [showCSR, setShowCSR] = useState(null);
  const [csrContent, setCsrContent] = useState({ client: '', server: '' });
  const [expandedSection, setExpandedSection] = useState('setup');

  useEffect(() => {
    loadConfig();
    loadStatistiche();
    loadCSRContent();
  }, []);

  const loadConfig = async () => {
    try {
      // Config endpoint doesn't require auth
      const response = await axios.get(`${API}/api/sdi/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading SDI config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistiche = async () => {
    try {
      const response = await axios.get(`${API}/api/sdi/statistiche`);
      setStatistiche(response.data);
    } catch (error) {
      console.error('Error loading SDI stats:', error);
    }
  };

  const loadCSRContent = async () => {
    try {
      // CSR endpoints don't require auth
      const [clientRes, serverRes] = await Promise.all([
        axios.get(`${API}/api/sdi/certificati/csr/client`, {
          responseType: 'text'
        }).catch(() => ({ data: '' })),
        axios.get(`${API}/api/sdi/certificati/csr/server`, {
          responseType: 'text'
        }).catch(() => ({ data: '' }))
      ]);
      setCsrContent({
        client: clientRes.data,
        server: serverRes.data
      });
    } catch (error) {
      console.error('Error loading CSR:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato negli appunti`);
  };

  const downloadCSR = (content, filename) => {
    const blob = new Blob([content], { type: 'application/pkcs10' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} scaricato`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-6 w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
            <Shield className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Integrazione SdI</h1>
            <p className="text-blue-100">Sistema di Interscambio - Agenzia delle Entrate</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">Codice Destinatario</p>
            <p className="text-xl font-bold font-mono">{config?.codice_destinatario || 'KRRH6B9'}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">Ambiente</p>
            <p className="text-xl font-bold">{config?.ambiente || 'TEST'}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">Stato</p>
            <div className="flex items-center gap-2">
              {config?.attivo ? (
                <><CheckCircle className="h-5 w-5 text-green-400" /><span className="font-bold">Attivo</span></>
              ) : (
                <><Clock className="h-5 w-5 text-yellow-400" /><span className="font-bold">Setup</span></>
              )}
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">Fatture Ricevute</p>
            <p className="text-xl font-bold">{statistiche?.fatture_totali || 0}</p>
          </div>
        </div>
      </div>

      {/* STEP 1: Certificati CSR */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 overflow-hidden">
        <button 
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
          onClick={() => setExpandedSection(expandedSection === 'csr' ? '' : 'csr')}
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              config?.certificati?.client_cert && config?.certificati?.server_cert 
                ? 'bg-green-100 text-green-600' 
                : 'bg-blue-100 text-blue-600'
            }`}>1</div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">Certificati CSR</p>
              <p className="text-sm text-gray-500">Genera e carica i certificati sul portale SdI</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config?.certificati?.client_csr && config?.certificati?.server_csr ? (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">CSR Pronti</span>
            ) : (
              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">Da generare</span>
            )}
            {expandedSection === 'csr' ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>
        </button>
        
        {expandedSection === 'csr' && (
          <div className="border-t border-gray-200 p-4 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📋 Istruzioni</h4>
              <ol className="text-sm text-blue-800 space-y-2">
                <li>1. Copia o scarica i file CSR qui sotto</li>
                <li>2. Vai su <a href="https://accreditamento.fatturapa.gov.it/" target="_blank" rel="noopener noreferrer" className="underline font-medium">accreditamento.fatturapa.gov.it</a></li>
                <li>3. Accedi con SPID/CIE/CNS</li>
                <li>4. Vai su <strong>Certificati → Carica CSR Client</strong> e carica il file client.csr</li>
                <li>5. Vai su <strong>Certificati → Carica CSR Server</strong> e carica il file server.csr</li>
                <li>6. Scarica i certificati firmati (.cer) e caricali qui</li>
              </ol>
            </div>

            {/* Client CSR */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-purple-500" />
                  <span className="font-semibold">CLIENT.CSR</span>
                  {config?.certificati?.client_csr && <CheckCircle className="h-4 w-4 text-green-500" />}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(csrContent.client, 'Client CSR')}>
                    <Copy className="h-4 w-4 mr-1" /> Copia
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadCSR(csrContent.client, 'client.csr')}>
                    <Download className="h-4 w-4 mr-1" /> Scarica
                  </Button>
                </div>
              </div>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto max-h-32">
                {csrContent.client || 'Caricamento...'}
              </pre>
            </div>

            {/* Server CSR */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-blue-500" />
                  <span className="font-semibold">SERVER.CSR</span>
                  {config?.certificati?.server_csr && <CheckCircle className="h-4 w-4 text-green-500" />}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(csrContent.server, 'Server CSR')}>
                    <Copy className="h-4 w-4 mr-1" /> Copia
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadCSR(csrContent.server, 'server.csr')}>
                    <Download className="h-4 w-4 mr-1" /> Scarica
                  </Button>
                </div>
              </div>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto max-h-32">
                {csrContent.server || 'Caricamento...'}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* STEP 2: Configurazione Endpoint */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 overflow-hidden">
        <button 
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
          onClick={() => setExpandedSection(expandedSection === 'endpoint' ? '' : 'endpoint')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">2</div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">Configurazione Endpoint</p>
              <p className="text-sm text-gray-500">URL da inserire sul portale SdI</p>
            </div>
          </div>
          {expandedSection === 'endpoint' ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </button>
        
        {expandedSection === 'endpoint' && (
          <div className="border-t border-gray-200 p-4 space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900">Attenzione</h4>
                  <p className="text-sm text-yellow-800">
                    Per ricevere fatture da SdI, serve un <strong>server pubblico con IP fisso e certificato SSL valido</strong>.
                    L'URL di preview di Emergent non è adatto per la produzione.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Endpoint Ricezione Fatture</label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={`${window.location.origin}/api/sdi/ricezione/fattura`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" onClick={() => copyToClipboard(`${window.location.origin}/api/sdi/ricezione/fattura`, 'Endpoint')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Endpoint Notifiche</label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={`${window.location.origin}/api/sdi/ricezione/notifica`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" onClick={() => copyToClipboard(`${window.location.origin}/api/sdi/ricezione/notifica`, 'Endpoint notifiche')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Health Check</label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={`${window.location.origin}/api/sdi/health`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" onClick={() => window.open(`${API}/sdi/health`, '_blank')}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* STEP 3: Test Interoperabilità */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 overflow-hidden">
        <button 
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
          onClick={() => setExpandedSection(expandedSection === 'test' ? '' : 'test')}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">3</div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">Test di Interoperabilità</p>
              <p className="text-sm text-gray-500">Verifica comunicazione con SdI</p>
            </div>
          </div>
          {expandedSection === 'test' ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </button>
        
        {expandedSection === 'test' && (
          <div className="border-t border-gray-200 p-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Procedura Test</h4>
              <ol className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  Dopo aver caricato i certificati, vai su "Test di interoperabilità" nel portale SdI
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  Esegui i test B2B/B2C (fatturazione tra privati)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  Attendi la PEC di conferma esito test
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0">4</span>
                  Se positivo, richiedi passaggio in PRODUZIONE
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Statistiche */}
      {statistiche && (statistiche.fatture_totali > 0 || statistiche.notifiche_totali > 0) && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Riepilogo Ricezione
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{statistiche.fatture_totali}</p>
              <p className="text-xs text-blue-600">Fatture Totali</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{statistiche.fatture_elaborate}</p>
              <p className="text-xs text-green-600">Elaborate</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600">{statistiche.fatture_da_elaborare}</p>
              <p className="text-xs text-yellow-600">Da Elaborare</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-purple-600">{statistiche.notifiche_totali}</p>
              <p className="text-xs text-purple-600">Notifiche</p>
            </div>
          </div>
        </div>
      )}

      {/* Link utili */}
      <div className="bg-gray-50 rounded-2xl p-4">
        <h3 className="font-semibold text-gray-900 mb-3">🔗 Link Utili</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <a 
            href="https://accreditamento.fatturapa.gov.it/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 hover:border-blue-300 transition-colors"
          >
            <ExternalLink className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">Portale Accreditamento SdI</span>
          </a>
          <a 
            href="https://www.fatturapa.gov.it/it/sistemainterscambio/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 hover:border-blue-300 transition-colors"
          >
            <ExternalLink className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">Documentazione SdI</span>
          </a>
          <a 
            href="https://ivaservizi.agenziaentrate.gov.it/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 hover:border-blue-300 transition-colors"
          >
            <ExternalLink className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">Cassetto Fiscale</span>
          </a>
          <a 
            href="https://www.agenziaentrate.gov.it/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 hover:border-blue-300 transition-colors"
          >
            <ExternalLink className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">Agenzia delle Entrate</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default SDIConfigPage;
